<template>
    <div class="content">
        <div class="md-layout">
            <div class="md-layout-item">
                <div class="custom-toolbar">
                    <div class="custom-toolbar-start">
                        <customer-filter-options
                            class="filter-steps--choices filter-status"
                            @onCustomerSelected="handleCustomerChanged"
                            :filter-customer-id="customerIdFilterValue"
                        />
                    </div>
                    <div class="custom-toolbar-end">
                        <search-component v-if="$root.isDesktop" @onSearch="handleFilterOrSearch" />
                    </div>
                </div>
            </div>
        </div>
        <div class="md-layout">
            <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
                <md-card>
                    <md-card-header class="md-card-header-icon md-card-header-green">
                        <div class="card-icon card-icon-text">
                            <md-icon>star</md-icon>
                            Frequent Addresses
                        </div>
                    </md-card-header>
                    <md-card-content>
                        <div class="cf-container">
                            <div class="cf-action-container">
                                <batch-upload
                                    v-if="!isCustomer && !isCustomerAdmin"
                                    :title="'Import Frequent Addresses'"
                                    :template-type="'frequentAddresses'"
                                    @batchImported="handleFrequentAddressCreated"
                                />

                                <md-button
                                    v-if="!isReadOnly"
                                    title="Add Frequent Address"
                                    class="md-primary md-just-icon md-round"
                                    @click="emitAdd"
                                >
                                    <md-icon>add</md-icon>
                                </md-button>
                            </div>
                            <div v-if="frequentAddressesList.length != 0">
                                <frequent-addresses-list
                                    :list-of-frequent-addresses="frequentAddressesList"
                                    :is-read-only="isReadOnly"
                                    @retrieve-frequent-addresses="fetchFrequentAddressesList"
                                ></frequent-addresses-list>
                            </div>
                            <div v-if="frequentAddressesList.length == 0">
                                <p class="no-result-message">No Frequent Addresses configured</p>
                            </div>
                        </div>
                    </md-card-content>
                    <md-card-actions class="page-footer" md-alignment="space-between">
                        <div>
                            <p v-if="total === pagination.perPage" class="card-category">
                                Page {{ pagination.currentPage }} of many
                            </p>
                            <p v-else class="card-category">Page {{ pagination.currentPage }} of {{ totalPages }}</p>
                            <p>Total Frequent Addresses: {{ pagination.total }}</p>
                        </div>
                        <pagination
                            v-model="pagination.currentPage"
                            class="pagination-no-border pagination-success"
                            :per-page="pagination.perPage"
                            :total="total"
                            :enable-first-page-link="true"
                            :enable-last-page-link="true"
                            @change-page="handleChangePage($event, pagination.perPage)"
                        />
                    </md-card-actions>
                </md-card>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { PAGINATION_DEFAULTS } from '@/utils/defaults';
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { CustomerFilterOptions, SearchComponent, Pagination, BatchUpload } from '@/components';
import FrequentAddressesList from './components/FrequentAddressesList';

export default {
    name: 'FrequentAddressesOverview',
    components: {
        FrequentAddressesList,
        CustomerFilterOptions,
        SearchComponent,
        Pagination,
        BatchUpload
    },
    mixins: [GeneralMixin],
    data() {
        return {
            frequentAddressesList: [],
            customerIdFilterValue: null,
            currentSearchTextValue: null,
            isReadOnly: true,
            pagination: PAGINATION_DEFAULTS,
            searchText: ''
        };
    },
    async mounted() {
        this.$_handleLoaderState(true);
        this.pagination.perPage = 50;
        await this.fetchFrequentAddressesList();
        this.checkIfReadOnly();
        this.$_handleLoaderState(false);
    },
    computed: {
        ...mapGetters({
            isCustomer: 'user/isCustomer',
            isCustomerAdmin: 'user/isCustomerAdmin',
            isSupportUser: 'user/isSupportUser',
            isSupportAdminUser: 'user/isSupportAdminUser',
            user: 'user/user'
        }),
        total() {
            return this.pagination.total;
        },
        totalPages() {
            if (this.total > 0) {
                return Math.ceil(this.total / this.pagination.perPage);
            }
            return 1;
        }
    },
    methods: {
        async fetchFrequentAddressesList() {
            let customerIdPayload = null;

            if (this.customerIdFilterValue) {
                customerIdPayload = this.customerIdFilterValue;
            }
            if (this.isCustomer || this.isCustomerAdmin) {
                customerIdPayload = this.user.customerId;
            }
            const api = `/api/frequent-addresses/list`;
            const payload = {
                method: 'get',
                params: {
                    searchText: this.searchText,
                    customerId: customerIdPayload,
                    pageNumber: this.pagination.currentPage,
                    itemsPerPage: this.pagination.perPage
                }
            };
            await handleRequests(api, payload).then(
                (response) => {
                    this.frequentAddressesList = response.data.frequentAddresses;
                    this.pagination.total = response.data.totalFrequentAddressesCount;
                },
                (error) => {
                    const message = 'Error in adding frequent address';
                    showErrorMessage(this, message, error);
                    this.$_handleLoaderState(false);
                }
            );
        },
        async handleCustomerChanged(value) {
            this.customerIdFilterValue = value;
            this.pagination.currentPage = 1; // move the user back to page 1
            await this.fetchFrequentAddressesList();
        },
        async handleFilterOrSearch(value) {
            this.searchText = value.searchText;
            this.pagination.currentPage = 1; // move the user back to page 1
            await this.fetchFrequentAddressesList();
        },
        emitAdd() {
            this.$router.push({
                name: 'Frequent Address Details',
                params: {
                    frequentAddressId: 0,
                    editMode: true
                }
            });
        },
        checkIfReadOnly() {
            if (!this.isCustomer) {
                this.isReadOnly = false;
            }
        },
        async handleChangePage(currentPage = 1, perPage = 50) {
            this.$_handleLoaderState(true);
            this.pagination.currentPage = currentPage;
            this.pagination.perPage = perPage;
            await this.fetchFrequentAddressesList();
            document.querySelector('.content').scrollIntoView(); // Only way for the page to scroll up
            this.$_handleLoaderState(false);
        },
        handleFrequentAddressCreated() {
            this.$_handleLoaderState(true);
            this.fetchFrequentAddressesList();
            this.$_handleLoaderState(false);
        }
    }
};
</script>

<style lang="scss" scoped>
.body-list {
    padding-top: 0px;
    position: relative;
}

.custom-toolbar {
    margin-top: 0px;
}

.content {
    margin-top: -15px;

    ::v-deep .md-card.md-theme-default {
        margin-bottom: 0;
    }
}

.card-icon-text {
    color: white;
}

::v-deep .filter-customer .vs__selected {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
    display: block;
}

::v-deep .md-card-actions {
    padding: 16px;
    &.page-footer {
        padding-top: 0px;
    }
}

.filter-status {
    min-width: 250px;
    margin-right: 0;
    min-height: initial;
}

.cf-action-container {
    display: flex;
    flex: 3;
}
</style>
